/**
 * Helpers for the project filter.
 *
 * The matching runs against data-search-index, which is generated on save by
 * Glunz\Search\ProjectIndex (library/Glunz/Search/ProjectIndex.php) and rendered
 * onto each project by template-parts/detail/projects-list.php. That attribute is a
 * deduplicated, space-separated bag of already-normalised words — every source word
 * appears twice, once with its diacritics stripped and once German-expanded:
 *
 *   "Zürich"           -> "zurich zuerich"
 *   "Alexandra Müller" -> "alexandra muller mueller"
 *
 * Storing both variants is what lets a query be found by typing zürich, zurich or
 * zuerich, and it means only the *query* has to be normalised here.
 */

/**
 * The exact twin of TextNormalizer::fold() in PHP. If these two ever drift, umlaut
 * queries stop matching silently — change both or neither.
 */
const normalize = (value) =>
  value.toLowerCase().replace(/ß/g, 'ss').normalize('NFD').replace(/[̀-ͯ]/g, '')

/**
 * Split on non-word characters, mirroring the PHP tokenizer — not on whitespace.
 * "5.6 Mio" is indexed as the words "5", "6" and "mio", so a query of "5.6" has to
 * break apart the same way to find it.
 */
const tokenize = (value) =>
  normalize(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)

/** How well a single query word matches: whole word beats start-of-word beats anywhere. */
const EXACT = 1
const PREFIX = 0.6
const PARTIAL = 0.3

const scoreWord = (needle, indexWords) => {
  let best = 0

  for (const word of indexWords) {
    if (word === needle) return EXACT // nothing can beat an exact hit, stop looking
    if (word.startsWith(needle)) best = Math.max(best, PREFIX)
    else if (word.includes(needle)) best = Math.max(best, PARTIAL)
  }

  return best
}

/**
 * How well a project matches a keyword, from 0 (no match) to 1 (every word hit
 * exactly). Useful for ordering results by relevance; ignore it and treat > 0 as a
 * hit if you only need to filter.
 *
 * Every word of the keyword must appear somewhere, in any order ("anker albert"
 * finds "Centre Albert Anker"), but they need not be adjacent — the index is a bag
 * of words, so phrase matching is not possible against it.
 *
 * An empty keyword scores 1: there is nothing to filter by, so everything matches.
 *
 * @param {string} keyword     Raw input from the search field.
 * @param {string} searchIndex The project's data-search-index value.
 * @returns {number} 0–1.
 */
export function scoreProject(keyword, searchIndex) {
  const needles = tokenize(keyword ?? '')

  if (needles.length === 0) return 1
  if (!searchIndex) return 0

  // The index is already normalised by PHP, so it only needs splitting.
  const indexWords = searchIndex.split(/\s+/).filter(Boolean)
  let total = 0

  for (const needle of needles) {
    const score = scoreWord(needle, indexWords)

    if (score === 0) return 0 // every word must match, so one miss ends it

    total += score
  }

  return total / needles.length
}

/**
 * Whether a project matches a keyword at all.
 *
 * @param {string} keyword     Raw input from the search field.
 * @param {string} searchIndex The project's data-search-index value.
 * @returns {boolean}
 */
export function matchesProject(keyword, searchIndex) {
  return scoreProject(keyword, searchIndex) > 0
}
